import React, { useState, useEffect } from 'react';
import { Calendar, Clock, User, Mail, MapPin } from 'lucide-react';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp, query, where, getDocs } from 'firebase/firestore';


// Add prop
export default function BookingSystem({ preSelectedService }) {
  
  // Update state initialization
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    location: 'studio',
    service: preSelectedService || 'deep-tissue',  // Use pre-selected
    date: '',
    time: '',
    duration: '60',
    message: ''
  });

  // Add useEffect to update when prop changes
  useEffect(() => {
    if (preSelectedService) {
      setFormData(prev => ({ ...prev, service: preSelectedService }));
    }
  }, [preSelectedService]);

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [bookedTimes, setBookedTimes] = useState([]);
  const [checkingAvailability, setCheckingAvailability] = useState(false);

   // Fetch booked times when date changes
  useEffect(() => {
    if (formData.date) {
      fetchBookedTimes(formData.date);
    } else {
      setBookedTimes([]);
    }
  }, [formData.date]);

  const fetchBookedTimes = async (selectedDate) => {
  setCheckingAvailability(true);
  try {
    // Query bookings for the selected date that are confirmed or pending
    const q = query(
      collection(db, 'bookings'),
      where('date', '==', selectedDate),
      where('status', 'in', ['pending', 'confirmed'])
    );

    const querySnapshot = await getDocs(q);
    const blockedTimes = [];
    
    querySnapshot.forEach((doc) => {
      const booking = doc.data();
      if (booking.time && booking.duration) {
        // Get all time slots that this booking blocks
        const affectedSlots = getAffectedTimeSlots(booking.time, parseInt(booking.duration));
        blockedTimes.push(...affectedSlots);
      }
    });

    // Remove duplicates
    setBookedTimes([...new Set(blockedTimes)]);
  } catch (error) {
    console.error('Error fetching booked times:', error);
  } finally {
    setCheckingAvailability(false);
  }
};

  // Helper function to calculate which time slots are blocked by a booking
  const getAffectedTimeSlots = (startTime, durationMinutes) => {
    const affectedSlots = [];
    
    // Convert start time to minutes
    const [hours, minutes] = startTime.split(':').map(Number);
    const startMinutes = hours * 60 + minutes;
    
    // Get all possible time slots for the day
    const allSlots = getAllTimeSlots();
    
    // Check each slot to see if it overlaps with this booking
    allSlots.forEach(slot => {
      const [slotHours, slotMinutes] = slot.split(':').map(Number);
      const slotStartMinutes = slotHours * 60 + slotMinutes;
      const slotEndMinutes = slotStartMinutes + 30; // Each slot is 30 minutes
      
      const bookingEndMinutes = startMinutes + durationMinutes;
      
      // Check if this slot overlaps with the booking
      // Slot overlaps if:
      // - Slot starts during the booking, OR
      // - Slot ends during the booking, OR
      // - Booking is entirely within the slot
      if (
        (slotStartMinutes >= startMinutes && slotStartMinutes < bookingEndMinutes) ||
        (slotEndMinutes > startMinutes && slotEndMinutes <= bookingEndMinutes) ||
        (slotStartMinutes <= startMinutes && slotEndMinutes >= bookingEndMinutes)
      ) {
        affectedSlots.push(slot);
      }
    });
    
    return affectedSlots;
  };


  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData({
      ...formData,
      [name]: value,
      // Reset time when date changes (in case available times are different)
      ...(name === 'date' && { time: '' })
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Add booking to Firebase
      const docRef = await addDoc(collection(db, 'bookings'), {
        ...formData,
        createdAt: serverTimestamp(),
        status: 'pending' // You can use this to track booking status
      });

      console.log('Booking saved with ID:', docRef.id);
      setSubmitted(true);
      
      // Reset form after 3 seconds
      setTimeout(() => {
        setSubmitted(false);
        setFormData({
          name: '',
          email: '',
          phone: '',
          service: 'deep-tissue',
          date: '',
          time: '',
          duration: '60',
          message: ''
        });
      }, 3000);

    } catch (err) {
      console.error('Error saving booking:', err);
      setError('Sorry, there was an error saving your booking. Please try again or call us directly.');
    } finally {
      setLoading(false);
    }
  };

  const services = [
    { value: 'deep-tissue', label: 'Deep Tissue Massage', price: '£50' },
    { value: 'swedish', label: 'Swedish Massage (45 mins)', price: '£40' },
    { value: 'lymphatic', label: 'Lymphatic Drainage Massage', price: '£40' },
     { value: 'brazilian', label: 'Brazilian Lymphatic Drainage', price: '£50' }
  ];

  // Get available time slots based on selected date
  const getAllTimeSlots  = () => {
    if (!formData.date) {
      return []; // No date selected yet
    }

    const selectedDate = new Date(formData.date + 'T00:00:00');
    const dayOfWeek = selectedDate.getDay(); // 0 = Sunday, 5 = Friday, 6 = Saturday


//sat (6) - day off
    if (dayOfWeek === 6) {
      return [
        
      ];
    }

    // Friday (5)
    if (dayOfWeek === 5) { //Friday 
      return [
        '15:30', '16:00', '16:30', '17:00','17:30'
      ];
    }

    
    //Sunday (0) - More hours available
    if (dayOfWeek === 0) {
      return [
         '13:00', '13:30', '14:00', '14:30', '15:00', '15:30', '16:00', '16:30'
      ];
    }

    // Monday to Thursday - Evening only
    return ['17:30','18:00', '18:30'];
  };

   const isTimeBooked = (time) => {
    return bookedTimes.includes(time);
  };

  const durations = [
    { value: '60', label: '60 minutes' },
  ];

  // Get day name for display
  const getDayInfo = () => {
    if (!formData.date) return '';
    
    const selectedDate = new Date(formData.date + 'T00:00:00');
    const dayOfWeek = selectedDate.getDay();
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    
    if (dayOfWeek === 5 || dayOfWeek === 0) {
      return `${dayNames[dayOfWeek]} - Extended hours available!`;
    }
     if (dayOfWeek === 6) {
      return `${dayNames[dayOfWeek]} - No hours available`;
    }
    return `${dayNames[dayOfWeek]} - Evening appointments only`;
  };

  const availableSlots = getAllTimeSlots().filter(time => !isTimeBooked(time));
  const hasAvailableSlots = availableSlots.length > 0;
  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-cyan-50">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
          
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Book Your Massage</h1>
            <p className="text-lg text-gray-600">Fill in the details below to reserve your session</p>
            <p className="text-sm text-pink-600 font-semibold mt-2">Ladies Only (Male clients by referral)</p>
          </div>

          {error && (
            <div className="bg-red-50 border-2 border-red-500 rounded-xl p-4 mb-6">
              <p className="text-red-800">{error}</p>
            </div>
          )}

          {submitted ? (
            <div className="bg-green-50 border-2 border-green-500 rounded-xl p-8 text-center">
              <div className="text-6xl mb-4">✓</div>
              <h2 className="text-3xl font-bold text-green-800 mb-2">Booking Received!</h2>
              <p className="text-lg text-green-700">
                Thank you for your booking. We'll contact you shortly to confirm your appointment.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Personal Information */}
              <div className="space-y-4">
                <h3 className="text-xl font-bold text-gray-900 flex items-center space-x-2">
                  <User className="text-teal-600" size={24} />
                  <span>Personal Information</span>
                </h3>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      disabled={loading}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:bg-gray-100"
                      placeholder="John Smith"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      disabled={loading}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:bg-gray-100"
                      placeholder="07123 456789"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    disabled={loading}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:bg-gray-100"
                    placeholder="john.smith@example.com"
                  />
                </div>
              </div>

              {/* Service Selection */}
              <div className="space-y-4">
                <h3 className="text-xl font-bold text-gray-900 flex items-center space-x-2">
                  <Calendar className="text-teal-600" size={24} />
                  <span>Service & Appointment</span>
                </h3>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Service *
                  </label>
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    required
                    disabled={loading}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:bg-gray-100"
                  >
                    {services.map(service => (
                      <option key={service.value} value={service.value}>
                        {service.label} - {service.price}
                      </option>
                    ))}
                  </select>
                </div>
                <select
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:bg-gray-100"
                >
                  <option value="studio">At My Location (Rugby)</option>
                  <option value="mobile">Mobile - I Come to You (within 10 miles of Rugby)</option>
                </select>
                {/*<div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Duration *
                  </label>
                  <select
                    name="duration"
                    value={formData.duration}
                    onChange={handleChange}
                    required
                    disabled={loading}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:bg-gray-100"
                  >
                    {durations.map(duration => (
                      <option key={duration.value} value={duration.value}>
                        {duration.label}
                      </option>
                    ))}
                  </select>
                </div>*/}

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Preferred Date *
                    </label>
                    <input
                      type="date"
                      name="date"
                      value={formData.date}
                      onChange={handleChange}
                      required
                      disabled={loading}
                      min={(() => {
                        const tomorrow = new Date();
                        tomorrow.setDate(tomorrow.getDate() + 1);
                        return tomorrow.toISOString().split('T')[0];
                      })()}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:bg-gray-100"
                    />
                    {formData.date && (
                      <p className="text-sm text-teal-600 mt-1">
                        {getDayInfo()}
                      </p>
                    )}
                  </div>

                   <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Preferred Time *
                    </label>
                    <select
                      name="time"
                      value={formData.time}
                      onChange={handleChange}
                      required
                      disabled={loading || !formData.date || checkingAvailability}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:bg-gray-100"
                    >
                      <option value="">
                        {!formData.date 
                          ? 'Please select a date first' 
                          : checkingAvailability 
                          ? 'Checking availability...'
                          : hasAvailableSlots
                          ? 'Select a time'
                          : 'No times available'}
                      </option>
                      {getAllTimeSlots().map(time => {
                        const booked = isTimeBooked(time);
                        return (
                          <option 
                            key={time} 
                            value={time}
                            disabled={booked}
                            className={booked ? 'text-gray-400' : ''}
                          >
                            {time} {booked ? '(Booked)' : ''}
                          </option>
                        );
                      })}
                    </select>
                    {formData.date && !checkingAvailability && (
                      <p className="text-sm mt-1">
                        {hasAvailableSlots ? (
                          <span className="text-gray-500">
                            {availableSlots.length} slot{availableSlots.length !== 1 ? 's' : ''} available
                          </span>
                        ) : (
                          <span className="text-red-600">
                            All slots booked for this date. Please choose another date.
                          </span>
                        )}
                      </p>
                    )}
                  </div>
                  </div>
            </div>

              {/* Additional Information */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Notes (Optional)
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="4"
                  disabled={loading}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:bg-gray-100"
                  placeholder="Any specific areas of focus, health concerns, or special requests..."
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-teal-600 text-white py-4 rounded-lg text-lg font-semibold hover:bg-teal-700 transition shadow-lg hover:shadow-xl disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {loading ? 'Saving Booking...' : 'Request Booking'}
              </button>

              <p className="text-sm text-gray-500 text-center">
                * Required fields. We'll contact you to confirm your appointment.
              </p>
            </form>
          )}
        </div>

        {/* Contact Info Box */}
        <div className="mt-8 bg-white rounded-xl shadow-lg p-6">
          <h3 className="font-bold text-gray-900 mb-4 text-center">Need Help?</h3>
          <div className="grid md:grid-cols-2 gap-4 text-center">
            <div className="flex flex-col items-center">
              <Mail className="text-teal-600 mb-2" size={24} />
              <p className="text-sm text-gray-600">hollismassage@hotmail.com</p>
            </div>
            <div className="flex flex-col items-center">
              <MapPin className="text-teal-600 mb-2" size={24} />
              <p className="text-sm text-gray-600">Rugby, Warwickshire</p>
            </div>
          </div>
        </div>

        {/* Availability Info */}
        <div className="mt-8 bg-teal-50 border border-teal-200 rounded-xl p-6">
          <h3 className="font-bold text-teal-900 mb-3 text-center">📅 Availability</h3>
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="font-semibold text-teal-800 mb-2">🕐 Monday - Thursday</p>
              <p className="text-teal-700">Evening appointments: 5:30pm - 7.30pm</p>
            </div>
            <div>
              <p className="font-semibold text-teal-800 mb-2">🕐 Friday - Sunday</p>
              <p className="text-teal-700">Extended hours</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}